// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Assistant voice mode. Listens on the microphone, cuts a turn when the
// person pauses, transcribes it on the server (Whisper), posts the text into the
// Assistant DM like a typed message and speaks the reply in the Konvo voice a
// sentence at a time, as the server streams it (KONVO_ASSISTANT_VOICE), so the
// first words play while the Assistant is still working. Speaking over the
// Assistant, or tapping the orb, interrupts it and stops the turn.
import {Endpoints} from '@app/features/app/constants/Endpoints';
import AssistantActivity from '@app/features/konvo_assistant/state/AssistantActivity';
import type {VoiceEngineAvailability} from '@app/features/konvo_assistant/state/AssistantSpeech';
import AssistantSpeech from '@app/features/konvo_assistant/state/AssistantSpeech';
import {KonvoVoicePlayer} from '@app/features/konvo_assistant/state/KonvoVoicePlayer';
import {describeScreen, pushScreenContext, screenGoesToHome} from '@app/features/konvo_assistant/state/ScreenContext';
import {CodexLiveEngine} from '@app/features/konvo_assistant/voice/CodexLiveEngine';
import {
	GeminiLiveEngine,
	type LiveLogEntry,
	type LiveState,
} from '@app/features/konvo_assistant/voice/GeminiLiveEngine';
import {
	BARGE_IN,
	LISTENING,
	levelDb,
	VoiceActivityDetector,
} from '@app/features/konvo_assistant/voice/VoiceActivityDetector';
import VoiceAgentsFocus from '@app/features/konvo_assistant/voice/VoiceAgentsFocus';
import VoiceModeDrafts, {type VoiceDraftCommand} from '@app/features/konvo_assistant/voice/VoiceModeDrafts';
import {cueLength, playVoiceModeCue, type VoiceModeCue} from '@app/features/konvo_assistant/voice/VoiceModeSounds';
import {pendingActionCardsOfLatestAnswer} from '@app/features/konvo_assistant/voice/VoiceModeSources';
import {VoiceReplySpeaker} from '@app/features/konvo_assistant/voice/VoiceReplySpeaker';
import * as MessageCommands from '@app/features/messaging/commands/MessageCommands';
import {ChannelMessages} from '@app/features/messaging/state/ChannelMessages';
import Messages from '@app/features/messaging/state/MessagingMessages';
import {http} from '@app/features/platform/transport/RestTransport';
import {formatMessageForTts} from '@app/features/voice/utils/TtsTextFormatter';
import VoiceTts from '@app/features/voice/utils/VoiceTtsUtils';
import {KONVOBOT_ID} from '@konvo/constants/src/AppConstants';
import type {Message as WireMessage} from '@konvo/schema/src/domains/message/MessageResponseSchemas';
import * as SnowflakeUtils from '@konvo/snowflake/src/SnowflakeUtils';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {comparer, makeAutoObservable, reaction, runInAction} from 'mobx';

export type VoiceModeState =
	| 'idle'
	| 'starting'
	| 'listening'
	| 'hearing'
	| 'transcribing'
	| 'thinking'
	| 'speaking'
	| 'error';
export type VoiceModeError =
	| 'mic-denied'
	| 'mic-missing'
	| 'unsupported'
	| 'transcription'
	| 'send'
	| 'no-reply'
	| 'connection';

/** One sentence of a reply as the server streams it, or the end of the turn. */
export interface VoiceSpeechEvent {
	/** The id of the person's message the reply answers. */
	turn: string | null;
	index: number;
	text?: string;
	/** Exactly the audio pieces to play, already being rendered on the server. */
	pieces?: Array<string>;
	done?: boolean;
	ok?: boolean;
}

interface TranscribeReply {
	text?: string;
	/** Small talk the server answered itself. */
	quick?: {text: string; end: boolean; pieces?: Array<string>};
	/** The question, posted by the server. */
	message?: WireMessage;

	/** "Send it", "wait" or "cancel" while a prepared message was waiting: not posted to the Assistant. */
	command?: VoiceDraftCommand;
}

interface VoiceTurn {
	id: string;
	/** Sentences have started arriving, so the reply is being spoken as it streams. */
	streaming: boolean;
	done: boolean;
}

/** When each stage of the current turn finished, from performance.now(). */
interface TurnMarks {
	speechEnd: number;
	uploaded?: number;
	sent?: number;
	firstSentence?: number;
	firstAudio?: number;
}

const TICK_MS = 50;
const MIN_RECORDING_BYTES = 1500;
const MAX_TURN_MS = 60_000;
const IDLE_RECORDING_RESTART_MS = 30_000;
const REPLY_SETTLE_MS = 700;
const REPLY_TIMEOUT_MS = 150_000;
const THINKING_CUE_EVERY_MS = 1800;
// If the server's end-of-turn never arrives, the end of the Assistant's turn still ends the reply.
const STREAM_END_GRACE_MS = 2000;
const MAX_EARLY_EVENTS = 64;
// What is on screen goes up alongside the transcription; the message waits this long for it at most.
const SCREEN_CONTEXT_WAIT_MS = 800;
const RECORDER_TYPES = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/webm'];

function pickRecorderType(): string | undefined {
	if (typeof MediaRecorder === 'undefined') return undefined;
	return RECORDER_TYPES.find((type) => MediaRecorder.isTypeSupported?.(type));
}

function isLaterId(id: string, than: string): boolean {
	try {
		return BigInt(id) > BigInt(than);
	} catch {
		return false;
	}
}

export class VoiceModeSession {
	state: VoiceModeState = 'idle';
	error: VoiceModeError | null = null;
	muted = false;
	userCaption = '';
	assistantCaption = '';
	/** The floating dock shows the full card (orb, captions) rather than the slim pill. */
	expanded = true;
	/** The agent's mute, type-instead and end buttons are up. */
	controlsOpen = false;
	/** 0..1 loudness for the orb (mic while listening, voice while speaking); read every frame, not observed. */
	level = 0;
	/** When the session started (ms since epoch), for the call timer. */
	startedAt = 0;
	/** The part of `assistantCaption` being spoken right now, when the voice reports it. */
	spokenRange: {start: number; end: number} | null = null;
	private spokenCursor = 0;
	private stopDraftWatch: (() => void) | null = null;

	private channelId: string | null = null;
	private i18n: I18n | null = null;
	private generation = 0;
	private context: AudioContext | null = null;
	private stream: MediaStream | null = null;
	private micAnalyser: AnalyserNode | null = null;
	private outputAnalyser: AnalyserNode | null = null;
	private player: KonvoVoicePlayer | null = null;
	private recorder: MediaRecorder | null = null;
	private recorderType: string | undefined;
	private chunks: Array<Blob> = [];
	private recordingSince = 0;
	private turnSince = 0;
	private samples = new Float32Array(1024);
	private detector = new VoiceActivityDetector(LISTENING);
	private ticker: ReturnType<typeof setInterval> | null = null;
	private lastTick = 0;
	private stopReplyWatch: (() => void) | null = null;
	private deviceVoice = false;
	private lastThinkingCue = 0;
	private speaker: VoiceReplySpeaker | null = null;
	private turn: VoiceTurn | null = null;
	/** Sentences that arrived before our own message came back from the server. */
	private early: Array<VoiceSpeechEvent> = [];
	private stopTurnSafety: (() => void) | null = null;
	private marks: TurnMarks | null = null;
	/** The last turn's stage timings, sent along with the next recording for the server logs. */
	private lastTimings: Record<string, number> | null = null;
	/** Gemini Live, when that is the chosen voice engine and it could start. */
	private live: GeminiLiveEngine | CodexLiveEngine | null = null;
	private thinkingPulse: ReturnType<typeof setInterval> | null = null;
	/** Gemini Live transcript lines not yet sent to the server's logs (it is an experiment). */
	private liveLog: Array<LiveLogEntry & {at: number}> = [];
	private liveLogSession = '';
	private liveLogStartedAt = 0;

	constructor() {
		makeAutoObservable<
			this,
			| 'level'
			| 'context'
			| 'stream'
			| 'micAnalyser'
			| 'outputAnalyser'
			| 'player'
			| 'recorder'
			| 'chunks'
			| 'samples'
			| 'detector'
			| 'ticker'
			| 'stopReplyWatch'
			| 'i18n'
			| 'speaker'
			| 'turn'
			| 'early'
			| 'stopTurnSafety'
			| 'marks'
			| 'lastTimings'
			| 'live'
			| 'thinkingPulse'
			| 'liveLog'
			| 'stopDraftWatch'
		>(
			this,
			{
				level: false,
				context: false,
				stream: false,
				micAnalyser: false,
				outputAnalyser: false,
				player: false,
				recorder: false,
				chunks: false,
				samples: false,
				detector: false,
				ticker: false,
				stopReplyWatch: false,
				i18n: false,
				speaker: false,
				turn: false,
				early: false,
				stopTurnSafety: false,
				marks: false,
				lastTimings: false,
				live: false,
				thinkingPulse: false,
				liveLog: false,
				stopDraftWatch: false,
			},
			{autoBind: true},
		);
	}

	get active(): boolean {
		return this.state !== 'idle';
	}

	/** The Assistant DM this session talks in. */
	get conversationId(): string | null {
		return this.channelId;
	}

	private clearAssistantCaption(): void {
		this.assistantCaption = '';
		this.spokenRange = null;
		this.spokenCursor = 0;
	}

	/** A piece started playing: find it in the caption (after what was already said) and mark it. */
	private markSpoken(text: string): void {
		const piece = text.trim();
		if (!piece) return;
		const start = this.assistantCaption.indexOf(piece, this.spokenCursor);
		if (start < 0) return;
		this.spokenRange = {start, end: start + piece.length};
		this.spokenCursor = start + piece.length;
	}

	setExpanded(value: boolean): void {
		this.expanded = value;
	}

	toggleControls(): void {
		this.controlsOpen = !this.controlsOpen;
	}

	closeControls(): void {
		this.controlsOpen = false;
	}

	/** Must run from a tap: browsers only allow the microphone and audio playback from a user gesture. */
	async start(channelId: string, i18n: I18n): Promise<void> {
		this.end();
		const generation = ++this.generation;
		this.channelId = channelId;
		this.i18n = i18n;
		this.error = null;
		this.muted = false;
		this.userCaption = '';
		this.clearAssistantCaption();
		this.startedAt = Date.now();
		// Messages and mail Konvo prepares show up as review cards; voice mode sends or holds them.
		const since = SnowflakeUtils.fromTimestamp(this.startedAt);
		const disposeCards = reaction(
			() => {
				void Messages.version;
				const messages = ChannelMessages.get(channelId)?.toArray() ?? [];
				return pendingActionCardsOfLatestAnswer(messages, KONVOBOT_ID, since);
			},
			(cards) => VoiceModeDrafts.offer(cards),
			{fireImmediately: true, equals: comparer.structural},
		);
		const disposeQuiet = reaction(
			() => this.state === 'listening' || this.state === 'hearing',
			(quiet) => VoiceModeDrafts.setQuiet(quiet),
			{fireImmediately: true},
		);
		this.stopDraftWatch = () => {
			disposeCards();
			disposeQuiet();
		};
		this.state = 'starting';
		this.expanded = true;
		this.controlsOpen = false;
		AssistantSpeech.setVoiceModeActive(true);
		// Unlock audio during the tap, before capability discovery or session negotiation.
		if (typeof AudioContext !== 'undefined') {
			this.context = new AudioContext();
			void this.context.resume().catch(() => undefined);
		}
		// Start with the engines this person had last time; the answer only refreshes them for next time.
		const discovery = (async () => {
			try {
				const response = await http.get<VoiceEngineAvailability>(Endpoints.KONVO_ASSISTANT_VOICE_ENGINES);
				if (response.body) AssistantSpeech.rememberVoiceEngines(response.body);
				return response.body ?? null;
			} catch {
				return null;
			}
		})();
		const known = AssistantSpeech.knownVoiceEngines ?? (await discovery);
		// Attempt the saved choice when capability discovery is temporarily unavailable.
		const engine = known ? AssistantSpeech.resolveVoiceEngine(known) : AssistantSpeech.voiceEngine;
		if (generation !== this.generation) return;
		if ((engine === 'codex-live' || engine === 'codex-voice') && (await this.startCodex(generation, engine))) return;
		if (engine === 'gemini' && (await this.startLive(generation))) return;
		if (generation !== this.generation) return;
		this.recorderType = pickRecorderType();
		if (!navigator.mediaDevices?.getUserMedia || this.recorderType === undefined) {
			this.fail('unsupported');
			return;
		}
		const context = this.context ?? new AudioContext();
		this.context = context;
		const output = context.createAnalyser();
		output.fftSize = 1024;
		output.connect(context.destination);
		this.outputAnalyser = output;
		this.player = new KonvoVoicePlayer((audio) => context.createMediaElementSource(audio).connect(output));
		this.player.prime();
		void context.resume().catch(() => undefined);
		let stream: MediaStream;
		try {
			stream = await navigator.mediaDevices.getUserMedia({
				audio: {echoCancellation: true, noiseSuppression: true, autoGainControl: true},
			});
		} catch (error) {
			if (generation !== this.generation) return;
			const name = error instanceof DOMException ? error.name : '';
			this.fail(name === 'NotFoundError' || name === 'OverconstrainedError' ? 'mic-missing' : 'mic-denied');
			return;
		}
		if (generation !== this.generation) {
			for (const track of stream.getTracks()) track.stop();
			return;
		}
		this.stream = stream;
		const mic = context.createAnalyser();
		mic.fftSize = 1024;
		context.createMediaStreamSource(stream).connect(mic);
		this.micAnalyser = mic;
		this.lastTick = performance.now();
		this.ticker = setInterval(this.tick, TICK_MS);
		this.cue('start');
		// The server gets the first answer ready: small-talk replies and openers rendered
		// in this voice, today at a glance, and a warm prompt cache.
		void http
			.post(Endpoints.KONVO_ASSISTANT_VOICE_START, {
				body: {
					voice: AssistantSpeech.effectiveVoiceId,
					speed: AssistantSpeech.rate,
					send_delay: AssistantSpeech.voiceSendDelay,
				},
			})
			.catch(() => undefined);
		this.listen();
	}

	private async startCodex(generation: number, mode: 'codex-live' | 'codex-voice'): Promise<boolean> {
		const live = new CodexLiveEngine(
			{
				mode,
				channelId: this.channelId!,
				voice: AssistantSpeech.codexVoice || undefined,
				sendDelay: AssistantSpeech.voiceSendDelay,
				context: () => describeScreen() ?? undefined,
				onState: (state) => {
					if (generation === this.generation && this.state !== 'error') this.setLiveState(state);
				},
				onLevel: (level) => {
					if (generation === this.generation) this.level = level;
				},
				onUserText: (text) => {
					if (generation === this.generation) this.setLiveUserText(text);
				},
				onAssistantText: (text) => {
					if (generation === this.generation) this.assistantCaption = text;
				},
				onError: (error) => {
					if (generation === this.generation) this.fail(error);
				},
				ask: (request) => this.askKonvo(request),
			},
			this.context,
		);
		this.live = live;
		try {
			await live.start();
		} catch {
			live.close();
			if (this.live === live) this.live = null;
			if (generation !== this.generation || this.error === 'mic-denied' || this.error === 'mic-missing') return true;
			this.error = null;
			this.state = 'starting';
			// A failed native connection may fall back; the saved preference is retained.
			try {
				const response = await http.get<VoiceEngineAvailability>(Endpoints.KONVO_ASSISTANT_VOICE_ENGINES);
				if (response.body?.gemini) return this.startLive(generation);
			} catch {}
			return false;
		}
		if (generation !== this.generation) {
			live.close();
			void live.audio.context?.close().catch(() => {});
			return true;
		}
		this.context = live.audio.context;
		this.state = 'listening';
		this.cue('start');
		return true;
	}

	/** "Send it", "wait" or "cancel" for the message Konvo prepared, answered with a word so they know it was heard. */
	private runDraftCommand(command: VoiceDraftCommand): void {
		if (VoiceModeDrafts.current?.agent) {
			void this.runAgentDraftCommand(command);
			return;
		}
		const i18n = this.i18n;
		let said: string;
		if (command === 'send') {
			void VoiceModeDrafts.send();
			said = i18n ? i18n._(msg`Sending it.`) : 'Sending it.';
		} else if (command === 'wait') {
			VoiceModeDrafts.hold();
			said = i18n ? i18n._(msg`Okay, I'll wait. Say send it when you're ready.`) : "Okay, I'll wait.";
		} else {
			void VoiceModeDrafts.cancel();
			said = i18n ? i18n._(msg`Cancelled. Nothing was sent.`) : 'Cancelled.';
		}
		this.assistantCaption = said;
		this.startSpeaking();
		this.speaker?.push(said);
		this.speaker?.end();
	}

	/**
	 * A coding-agent card answered by voice: "yes" confirms it (the card's own Confirm) and Konvo says
	 * what happened, in the card's words; "no" cancels it; "wait" keeps it on the card.
	 */
	private async runAgentDraftCommand(command: VoiceDraftCommand): Promise<void> {
		const generation = this.generation;
		const said = await this.agentDraftOutcome(command);
		if (generation !== this.generation) return;
		this.assistantCaption = said;
		this.startSpeaking();
		this.speaker?.push(said);
		this.speaker?.end();
	}

	/** Runs the command on the waiting agent card and returns what to say about it. */
	private async agentDraftOutcome(command: VoiceDraftCommand): Promise<string> {
		const i18n = this.i18n;
		const say = (descriptor: Parameters<I18n['_']>[0], fallback: string) => (i18n ? i18n._(descriptor) : fallback);
		if (command === 'wait') {
			VoiceModeDrafts.hold();
			return say(msg`Okay, it's waiting on the card. Say yes when you're ready.`, "Okay, it's waiting.");
		}
		if (command === 'cancel') {
			void VoiceModeDrafts.cancel();
			return say(msg`Cancelled. Nothing was sent.`, 'Cancelled.');
		}
		const draft = VoiceModeDrafts.current;
		// The live engines keep their own state; with the Konvo voices the orb thinks meanwhile.
		if (!this.live) this.state = 'thinking';
		await VoiceModeDrafts.send();
		// The card's own words: what happened on the machine or GitHub, or why nothing did.
		if (draft?.note) return draft.note;
		return draft?.phase === 'failed'
			? say(msg`I couldn't confirm it went through. Check the card in your Konvo chat.`, "I couldn't confirm it.")
			: say(msg`Done.`, 'Done.');
	}

	/** Change it: Konvo asks what to change and listens; the card keeps waiting until a new one replaces it. */
	askForChange(): void {
		VoiceModeDrafts.markChanging();
		const said = this.i18n ? this.i18n._(msg`What should I change?`) : 'What should I change?';
		if (this.live) {
			this.assistantCaption = said;
			return;
		}
		if (this.state === 'idle' || this.state === 'error') return;
		this.stopReply();
		this.assistantCaption = said;
		this.startSpeaking();
		this.speaker?.push(said);
		this.speaker?.end();
	}

	/** The live engines' transcript of what the person said; a new utterance ends what the last answer pointed at. */
	private setLiveUserText(text: string): void {
		const before = this.userCaption.trim();
		if (text.trim() && (!before || !text.trim().startsWith(before.slice(0, 12)))) VoiceAgentsFocus.clear();
		this.userCaption = text;
	}

	/** Tap on the orb: interrupt the Assistant, or send what has been said so far. */
	/** Gemini Live: Google hears and speaks; the Konvo Assistant answers through ask_konvo. False to fall back. */
	private async startLive(generation: number): Promise<boolean> {
		this.liveLogSession = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
		this.liveLogStartedAt = Date.now();
		this.liveLog = [];
		const live = new GeminiLiveEngine({
			onState: (state) => {
				if (generation === this.generation && this.state !== 'error') this.setLiveState(state);
			},
			onLevel: (level) => {
				this.level = level;
			},
			onUserText: (text) => {
				if (generation === this.generation) this.setLiveUserText(text);
			},
			onAssistantText: (text) => {
				if (generation === this.generation) this.assistantCaption = text;
			},
			onError: (error) => {
				if (generation === this.generation) this.fail(error);
			},
			ask: (request) => this.askKonvo(request),
			voice: AssistantSpeech.geminiVoice || undefined,
			onLog: (entry) => this.logLive(entry),
			onTurnDone: () => this.flushLiveLog(),
		});
		this.live = live;
		try {
			await live.start();
		} catch (error) {
			this.logLive({
				kind: 'event',
				text: `fell back to the Konvo voices: ${error instanceof Error ? error.message : 'error'}`,
			});
			this.flushLiveLog();
			// Fall back to the Konvo voices; this attempt's audio is not needed.
			void live.audio.context?.close().catch(() => undefined);
			if (this.live === live) this.live = null;
			return false;
		}
		if (generation !== this.generation) {
			live.close();
			return true;
		}
		if (this.context && this.context !== live.audio.context) void this.context.close().catch(() => undefined);
		this.context = live.audio.context;
		if (this.state === 'error') return true;
		this.state = 'listening';
		this.logLive({kind: 'event', text: 'started'});
		this.cue('start');
		return true;
	}

	private logLive(entry: LiveLogEntry): void {
		if (this.liveLog.length < 200) this.liveLog.push({...entry, at: Date.now() - this.liveLogStartedAt});
	}

	private flushLiveLog(): void {
		if (this.liveLog.length === 0) return;
		const entries = this.liveLog;
		this.liveLog = [];
		void http
			.post(Endpoints.KONVO_ASSISTANT_VOICE_LIVE_LOG, {body: {session: this.liveLogSession, entries}})
			.catch(() => undefined);
	}

	private setLiveState(state: LiveState): void {
		if (state === 'thinking' && !this.thinkingPulse) {
			this.cue('thinking');
			this.thinkingPulse = setInterval(() => this.cue('thinking'), THINKING_CUE_EVERY_MS);
		} else if (state !== 'thinking' && this.thinkingPulse) {
			clearInterval(this.thinkingPulse);
			this.thinkingPulse = null;
		}
		this.state = state;
	}

	/** Gemini's ask_konvo: the request goes to the Assistant with what is on screen, and its answer comes back to be spoken. */
	private async askKonvo(request: string): Promise<string> {
		const onHome = screenGoesToHome();
		if (onHome) await pushScreenContext();
		// A card is waiting: the person's own words ("yes", "cancel") may answer it, not the Assistant.
		const draft = VoiceModeDrafts.voiceHeader;
		const heard = this.userCaption.trim();
		const response = await http.post<{text?: string; command?: VoiceDraftCommand}>(
			Endpoints.KONVO_ASSISTANT_VOICE_ASK,
			{
				body: {
					request,
					channel_id: this.channelId,
					screen: onHome ? undefined : (describeScreen() ?? undefined),
					...(draft && heard ? {draft, heard} : {}),
				},
			},
		);
		const command = response.body?.command;
		if (command) {
			if (VoiceModeDrafts.current?.agent) return this.agentDraftOutcome(command);
			if (command === 'send') await VoiceModeDrafts.send();
			else if (command === 'wait') VoiceModeDrafts.hold();
			else await VoiceModeDrafts.cancel();
			return command === 'send' ? 'Sent.' : command === 'wait' ? 'It is waiting.' : 'Cancelled. Nothing was sent.';
		}
		return response.body?.text || "The Assistant didn't answer. Try again in a moment.";
	}

	tap(): void {
		if (this.live) {
			this.live.interrupt();
			return;
		}
		if (this.state === 'speaking') {
			this.cue('interrupt');
			this.interruptReply();
			this.listen();
		} else if (this.state === 'hearing') void this.finishTurn();
	}

	toggleMute(): void {
		this.muted = !this.muted;
		this.cue(this.muted ? 'mute' : 'unmute');
		if (this.live) {
			this.live.setMuted(this.muted);
			return;
		}
		for (const track of this.stream?.getAudioTracks() ?? []) track.enabled = !this.muted;
		if (this.muted && (this.state === 'hearing' || this.state === 'listening')) this.listen();
	}

	end(): void {
		const wasActive = this.state !== 'idle';
		const wasCodex = this.live instanceof CodexLiveEngine;
		this.stopDraftWatch?.();
		this.stopDraftWatch = null;
		VoiceModeDrafts.reset();
		VoiceAgentsFocus.clear();
		this.generation += 1;
		if (this.live) {
			if (this.live instanceof GeminiLiveEngine) this.logLive({kind: 'event', text: 'ended'});
			this.flushLiveLog();
		}
		this.live?.close();
		this.live = null;
		if (this.thinkingPulse) clearInterval(this.thinkingPulse);
		this.thinkingPulse = null;
		if (this.ticker) clearInterval(this.ticker);
		this.ticker = null;
		this.stopReplyWatch?.();
		this.stopReplyWatch = null;
		this.stopReply();
		this.early = [];
		this.marks = null;
		// The last turn's timings would otherwise ride on a recording that never comes.
		const timings = this.lastTimings;
		this.lastTimings = null;
		this.discardRecording();
		for (const track of this.stream?.getTracks() ?? []) track.stop();
		this.stream = null;
		const context = this.context;
		if (context && wasActive) {
			// Let the closing chime ring out before the audio context goes away.
			try {
				playVoiceModeCue(context, 'end');
			} catch {
				// No chime, still close.
			}
			setTimeout(() => void context.close().catch(() => undefined), cueLength('end') * 1000 + 100);
		} else {
			void context?.close().catch(() => undefined);
		}
		this.context = null;
		this.micAnalyser = null;
		this.outputAnalyser = null;
		this.player = null;
		this.level = 0;
		this.state = 'idle';
		this.controlsOpen = false;
		AssistantSpeech.setVoiceModeActive(false);
		// Codex closes through its session-ID route; an older generic end must not clear a replacement session.
		if (wasActive && !wasCodex) {
			void http.post(Endpoints.KONVO_ASSISTANT_VOICE_END, {body: timings ? {timings} : {}}).catch(() => undefined);
		}
	}

	private cue(cue: VoiceModeCue): void {
		const context = this.context;
		if (!context || context.state === 'closed') return;
		try {
			playVoiceModeCue(context, cue);
		} catch {
			// A missing sound never breaks voice mode.
		}
	}

	private fail(error: VoiceModeError): void {
		if (this.state !== 'error') this.cue('error');
		this.error = error;
		this.state = 'error';
		this.stopReplyWatch?.();
		this.stopReplyWatch = null;
		this.discardRecording();
	}

	/** Silences whatever reply is playing and forgets its turn. */
	private stopReply(): void {
		this.speaker?.stop();
		this.speaker = null;
		this.player?.stop();
		if (this.deviceVoice) VoiceTts.stop();
		this.deviceVoice = false;
		this.stopTurnSafety?.();
		this.stopTurnSafety = null;
		this.turn = null;
	}

	/** Talking over the Assistant also stops its turn, or the next question would wait behind the rest of it. */
	private interruptReply(): void {
		const turn = this.turn;
		if (!turn || turn.done || !AssistantActivity.running) return;
		void http.post(Endpoints.KONVO_ASSISTANT_CANCEL, {body: {}}).catch(() => undefined);
	}

	private listen(): void {
		this.stopReply();
		this.stopReplyWatch?.();
		this.stopReplyWatch = null;
		this.error = null;
		this.state = 'listening';
		this.detector.setOptions(LISTENING);
		this.startRecording();
	}

	private tick(): void {
		const now = performance.now();
		const elapsed = Math.min(250, now - this.lastTick);
		this.lastTick = now;
		const mic = this.micAnalyser;
		if (!mic) return;
		mic.getFloatTimeDomainData(this.samples);
		const db = this.muted ? -100 : levelDb(this.samples);
		const state = this.state;
		if (state === 'listening' || state === 'hearing') {
			const event = this.detector.push(db, elapsed);
			this.level = this.detector.intensity(db);
			if (event === 'speech-start') {
				this.state = 'hearing';
				this.turnSince = now;
			} else if (event === 'speech-end' || (state === 'hearing' && now - this.turnSince > MAX_TURN_MS)) {
				void this.finishTurn();
			} else if (state === 'listening' && now - this.recordingSince > IDLE_RECORDING_RESTART_MS) {
				this.startRecording();
			}
		} else if (state === 'speaking') {
			if (this.detector.push(db, elapsed) === 'speech-start') {
				this.cue('interrupt');
				this.interruptReply();
				this.listen();
				this.state = 'hearing';
				this.turnSince = now;
				return;
			}
			this.level = this.outputLevel(now);
		} else {
			this.level = 0;
			if ((state === 'thinking' || state === 'transcribing') && now - this.lastThinkingCue >= THINKING_CUE_EVERY_MS) {
				this.lastThinkingCue = now;
				this.cue('thinking');
			}
		}
	}

	private outputLevel(now: number): number {
		if (this.deviceVoice || !this.outputAnalyser) return 0.35 + 0.25 * Math.sin(now / 140) * Math.sin(now / 370);
		this.outputAnalyser.getFloatTimeDomainData(this.samples);
		return Math.min(1, Math.max(0, (levelDb(this.samples) + 50) / 35));
	}

	private startRecording(): void {
		this.discardRecording();
		if (!this.stream) return;
		const recorder = new MediaRecorder(this.stream, {mimeType: this.recorderType, audioBitsPerSecond: 32_000});
		const chunks: Array<Blob> = [];
		recorder.ondataavailable = (event) => {
			if (event.data.size > 0) chunks.push(event.data);
		};
		recorder.start(250);
		this.recorder = recorder;
		this.chunks = chunks;
		this.recordingSince = performance.now();
	}

	private discardRecording(): void {
		const recorder = this.recorder;
		this.recorder = null;
		this.chunks = [];
		if (recorder && recorder.state !== 'inactive') {
			recorder.ondataavailable = null;
			recorder.stop();
		}
	}

	private stopRecording(): Promise<Blob | null> {
		const recorder = this.recorder;
		const chunks = this.chunks;
		this.recorder = null;
		this.chunks = [];
		if (!recorder || recorder.state === 'inactive') return Promise.resolve(null);
		return new Promise((resolve) => {
			recorder.onstop = () => resolve(new Blob(chunks, {type: recorder.mimeType || this.recorderType}));
			recorder.stop();
		});
	}

	private async finishTurn(): Promise<void> {
		const generation = this.generation;
		this.state = 'transcribing';
		this.level = 0;
		this.lastThinkingCue = performance.now();
		this.marks = {speechEnd: performance.now()};
		this.early = [];
		// Taken now, while the screen still shows what they were talking about.
		const postsScreen = !screenGoesToHome();
		const screen = pushScreenContext();
		const audio = await this.stopRecording();
		if (generation !== this.generation) return;
		if (!audio || audio.size < MIN_RECORDING_BYTES) {
			this.listen();
			return;
		}
		this.cue('heard');
		// The first thinking pulse lands a moment after the "heard" lift, then repeats.
		this.lastThinkingCue = performance.now() - THINKING_CUE_EVERY_MS + 900;
		let text = '';
		let reply: TranscribeReply | null = null;
		try {
			const timings = this.lastTimings;
			this.lastTimings = null;
			// The server posts the question itself once it is transcribed (and starts the
			// Assistant while Whisper listens), unless it is small talk it can answer.
			const response = await http.post<TranscribeReply>(Endpoints.KONVO_ASSISTANT_VOICE_TRANSCRIBE, {
				body: audio,
				headers: {
					'Content-Type': audio.type.split(';')[0] || 'audio/webm',
					...(this.channelId ? {'X-Konvo-Voice-Send': this.channelId} : {}),
					...(postsScreen ? {'X-Konvo-Screen': '1'} : {}),
					...(timings ? {'X-Konvo-Voice-Timings': JSON.stringify(timings)} : {}),
					...(VoiceModeDrafts.voiceHeader ? {'X-Konvo-Voice-Draft': VoiceModeDrafts.voiceHeader} : {}),
				},
			});
			reply = response.body ?? null;
			text = reply?.text?.trim() ?? '';
			if (this.marks) this.marks.uploaded = performance.now();
		} catch {
			if (generation === this.generation) this.fail('transcription');
			return;
		}
		if (generation !== this.generation) return;
		if (!text) {
			this.listen();
			return;
		}
		this.userCaption = text;
		this.clearAssistantCaption();
		// A new question: what the last answer pointed at is done with.
		VoiceAgentsFocus.clear();
		if (reply?.command) {
			this.runDraftCommand(reply.command);
			return;
		}
		const quick = reply?.quick;
		if (quick) {
			// Small talk, answered on the spot; its audio was rendered when voice mode opened.
			this.assistantCaption = quick.text;
			this.startSpeaking(quick.end ? 'end' : 'listen');
			this.speaker?.pushPieces(quick.pieces?.length ? quick.pieces : [quick.text]);
			this.speaker?.end();
			return;
		}
		const channelId = this.channelId;
		if (reply?.message && channelId) {
			Messages.handleIncomingMessage({channelId, message: reply.message});
			this.beginTurn(channelId, reply.message.id, generation);
			return;
		}
		// An older server: send the question from here.
		await Promise.race([screen, new Promise((resolve) => setTimeout(resolve, SCREEN_CONTEXT_WAIT_MS))]);
		if (generation !== this.generation) return;
		await this.send(text, generation);
	}

	private async send(text: string, generation: number): Promise<void> {
		const channelId = this.channelId;
		if (!channelId) return;
		this.state = 'thinking';
		let sentId: string | null = null;
		try {
			const sent = await MessageCommands.send(channelId, {
				content: text,
				nonce: SnowflakeUtils.fromTimestamp(Date.now()),
			});
			sentId = sent?.id ?? null;
		} catch {
			sentId = null;
		}
		if (generation !== this.generation) return;
		if (!sentId) {
			this.fail('send');
			return;
		}
		this.beginTurn(channelId, sentId, generation);
	}

	private beginTurn(channelId: string, sentId: string, generation: number): void {
		this.state = 'thinking';
		if (this.marks) this.marks.sent = performance.now();
		this.turn = {id: sentId, streaming: false, done: false};
		// Until sentences stream in, fall back to reading the finished reply message.
		this.watchReply(channelId, sentId, generation);
		const early = this.early.filter((event) => event.turn === sentId);
		this.early = [];
		for (const event of early) this.receiveSpeech(event);
	}

	/** A sentence of the reply (or the end of the turn), pushed by the server while the Assistant works. */
	receiveSpeech(event: VoiceSpeechEvent): void {
		if (this.state === 'idle' || this.state === 'error') return;
		const turn = this.turn;
		if (!turn) {
			// Our message may still be on its way back from the server.
			if ((this.state === 'thinking' || this.state === 'transcribing') && this.early.length < MAX_EARLY_EVENTS) {
				this.early.push(event);
			}
			return;
		}
		if (event.turn && event.turn !== turn.id) return;
		const text = typeof event.text === 'string' ? event.text.trim() : '';
		if (text) {
			if (!turn.streaming) this.startStreamedReply(turn);
			if (this.marks) this.marks.firstSentence ??= performance.now();
			const spoken = this.i18n ? formatMessageForTts(text, '', null, this.i18n) : text;
			this.assistantCaption = this.assistantCaption ? `${this.assistantCaption} ${spoken}` : spoken;
			// The server names the exact pieces it is already rendering, so these requests hit its cache.
			if (event.pieces?.length) this.speaker?.pushPieces(event.pieces);
			else this.speaker?.push(spoken);
		}
		if (event.done && !turn.done) {
			turn.done = true;
			// With nothing streamed (an error, or a provider that doesn't stream) the reply watcher reads the message.
			if (turn.streaming) this.speaker?.end();
		}
	}

	private startStreamedReply(turn: VoiceTurn): void {
		turn.streaming = true;
		this.stopReplyWatch?.();
		this.stopReplyWatch = null;
		this.clearAssistantCaption();
		this.startSpeaking();
		let grace: ReturnType<typeof setTimeout> | null = null;
		const dispose = reaction(
			() => AssistantActivity.running,
			(running) => {
				if (grace) clearTimeout(grace);
				grace = running
					? null
					: setTimeout(() => {
							if (this.turn !== turn || turn.done) return;
							turn.done = true;
							this.speaker?.end();
						}, STREAM_END_GRACE_MS);
			},
		);
		this.stopTurnSafety = () => {
			dispose();
			if (grace) clearTimeout(grace);
		};
	}

	private startSpeaking(after: 'listen' | 'end' = 'listen'): void {
		const generation = this.generation;
		this.speaker?.stop();
		this.state = 'speaking';
		this.detector.setOptions(BARGE_IN);
		this.discardRecording();
		this.deviceVoice = false;
		this.speaker = new VoiceReplySpeaker(this.player, {
			voiceId: AssistantSpeech.effectiveVoiceId,
			rate: AssistantSpeech.rate,
			onStart: () => {
				if (this.marks) this.marks.firstAudio ??= performance.now();
			},
			onDone: () => {
				if (generation !== this.generation || this.state !== 'speaking') return;
				this.recordTimings();
				if (after === 'end') this.end();
				else this.listen();
			},
			onPiece: (text) => {
				if (generation === this.generation) this.markSpoken(text);
			},
			onDeviceVoice: () => {
				this.deviceVoice = true;
			},
		});
	}

	private recordTimings(): void {
		const marks = this.marks;
		this.marks = null;
		if (!marks) return;
		const since = (at: number | undefined) => (at === undefined ? undefined : Math.round(at - marks.speechEnd));
		const timings = {
			endpointMs: LISTENING.endMs,
			uploadMs: since(marks.uploaded),
			sendMs: since(marks.sent),
			firstSentenceMs: since(marks.firstSentence),
			firstAudioMs: since(marks.firstAudio),
			turnMs: since(performance.now()),
		};
		this.lastTimings = Object.fromEntries(
			Object.entries(timings).filter((entry): entry is [string, number] => entry[1] !== undefined),
		);
	}

	/** The reply is every Assistant message after ours, once its turn has finished (streamed replies are edited until then). */
	private watchReply(channelId: string, afterId: string, generation: number): void {
		let settle: ReturnType<typeof setTimeout> | null = null;
		const collect = () =>
			(ChannelMessages.get(channelId)?.toArray() ?? []).filter(
				(message) => message.author.id === KONVOBOT_ID && isLaterId(message.id, afterId),
			);
		const timeout = setTimeout(() => {
			if (generation === this.generation) this.fail('no-reply');
		}, REPLY_TIMEOUT_MS);
		const dispose = reaction(
			() => {
				void Messages.version;
				return {count: collect().length, running: AssistantActivity.running};
			},
			({count, running}) => {
				if (settle) clearTimeout(settle);
				settle = null;
				if (count === 0 || running) return;
				settle = setTimeout(() => {
					if (generation !== this.generation || AssistantActivity.running) return;
					const replies = collect();
					if (replies.length === 0) return;
					stop();
					this.speak(replies.map((message) => message.content).join('\n\n'));
				}, REPLY_SETTLE_MS);
			},
			{fireImmediately: true},
		);
		const stop = () => {
			dispose();
			clearTimeout(timeout);
			if (settle) clearTimeout(settle);
		};
		this.stopReplyWatch = stop;
	}

	private speak(content: string): void {
		const text = this.i18n ? formatMessageForTts(content, '', null, this.i18n) : content;
		this.stopReplyWatch = null;
		this.assistantCaption = text;
		this.startSpeaking();
		this.speaker?.push(text);
		this.speaker?.end();
	}
}

const VoiceMode = new VoiceModeSession();
export default VoiceMode;

// Development builds (the sandbox) can drive voice mode's look without a microphone or voice service.
if (import.meta.env.DEV && typeof window !== 'undefined') {
	(window as unknown as {__konvoVoiceMode?: unknown}).__konvoVoiceMode = {
		session: VoiceMode,
		// Voice for agents: offer review cards and point at Agents rows without a voice turn.
		drafts: VoiceModeDrafts,
		agentsFocus: VoiceAgentsFocus,
		set: (
			patch: Partial<
				Pick<
					VoiceModeSession,
					'state' | 'muted' | 'userCaption' | 'assistantCaption' | 'spokenRange' | 'startedAt' | 'controlsOpen'
				>
			>,
		) => runInAction(() => Object.assign(VoiceMode, patch)),
	};
}

/**
 * Start voice mode in an Assistant DM, or bring the running session's card back up.
 * Call from the tap itself so the browser lets voice mode use the microphone and play audio.
 */
export function openVoiceMode(channelId: string, i18n: I18n): void {
	if (VoiceMode.active && VoiceMode.state !== 'error' && VoiceMode.conversationId === channelId) {
		VoiceMode.setExpanded(true);
		return;
	}
	void VoiceMode.start(channelId, i18n);
}
